import {
  WSGrid,
  WSMarginProps,
  WSPanel
} from "@wingspanhq/fe-component-library";
import { Payer } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { InfoItem, InfoItemProps } from "./InfoItem";

import styles from "./styles.module.scss";

export interface ClientInfoPanelProps extends WSMarginProps {
  client: Payer;
}

/**
 * The client's identity, straight off the v3 `Payer`.
 *
 * v1 derived these rows through `wsName` over a user + member profile. On v3 a
 * contractor's view of a client carries no account or compliance entity — legal
 * name, address and structure live on the client's own account behind an
 * information-sharing grant — so every row here reads a `Payer` field directly.
 * Rows without a value are dropped, which on a sparsely-populated client can
 * leave the panel showing only the DBA.
 */
export const ClientInfoPanel: React.FC<ClientInfoPanelProps> = ({
  client,
  ...elementProps
}) => {
  const fields = useMemo(() => {
    const result: InfoItemProps[] = [
      {
        label: "DBA (doing business as)",
        value: client.profile?.company?.doingBusinessAs
      },
      {
        label: "Name",
        value: client.profile?.displayName
      },
      {
        label: "Email",
        value: client.email
      },
      {
        // v1 showed city + country off the member profile address. v3 carries
        // only the country on the relationship (`Payer.countryCode`); the rest
        // of the address is behind the information-sharing grant.
        label: "Location",
        value: client.countryCode
      },
      {
        label: "External ID",
        value: client.externalId ?? undefined
      },
      {
        label: "Additional emails",
        value: client.notificationCcEmails?.join(", ")
      }
    ];

    return result.filter(field => field.value);
  }, [client]);

  return (
    <WSPanel className={styles.panel} {...elementProps}>
      <WSGrid gutter="2XL">
        {fields.map(({ label, value }) => (
          <WSGrid.Item key={label} span={{ xs: "12", s: "6", l: "6" }}>
            <InfoItem label={label} value={value} />
          </WSGrid.Item>
        ))}
      </WSGrid>
    </WSPanel>
  );
};
